// Real Test Results Calculator - Calculates actual results based on test data
import { getTestById, getTestResultsByTestId, saveTestResult } from '../firebase/collections';

/**
 * Calculate real test results based on user answers and test configuration
 * @param {Object} userAnswers - User's answers object
 * @param {Object} testData - Test configuration and questions
 * @param {Object} userData - User information
 * @returns {Object} Calculated test results
 */
export const calculateRealTestResults = async (userAnswers, testData, userData) => {
  const {
    questions = [],
    duration = 60 * 60,
    title = '',
    exam = '',
    testConfig = null,
    markingScheme = { correct: 4, incorrect: -1, unattempted: 0 }
  } = testData;

  const {
    startTime = Date.now(),
    endTime = Date.now(),
    answers = {},
    timeSpent = {}
  } = userAnswers;

  // Calculate time metrics first
  const actualTimeSpent = Math.floor((endTime - startTime) / 1000); // Convert to seconds
  const timeUtilization = Math.round((actualTimeSpent / duration) * 100);

  // Calculate basic metrics
  let correctAnswers = 0;
  let wrongAnswers = 0;
  let unattempted = 0;
  let totalScore = 0;
  let totalTimeSpent = 0;
  const detailedResults = [];

  // Process each question
  questions.forEach((question, index) => {
    const userAnswer = answers[question.id];
    const questionTimeSpent = timeSpent[question.id] || 0;
    totalTimeSpent += questionTimeSpent;

    let isCorrect = false;
    let questionScore = 0;
    let questionStatus = 'unattempted';

    // More robust check for unattempted questions
    const isAttempted = userAnswer !== null && 
                       userAnswer !== undefined && 
                       userAnswer !== '' && 
                       userAnswer !== 'null' && 
                       userAnswer !== 'undefined';

    if (isAttempted) {
      // Check if answer is correct based on question type
      isCorrect = checkAnswerCorrectness(question, userAnswer);
      
      if (isCorrect) {
        correctAnswers++;
        questionScore = question.markingScheme?.correct || markingScheme.correct;
        questionStatus = 'correct';
      } else {
        wrongAnswers++;
        questionScore = question.markingScheme?.incorrect || markingScheme.incorrect;
        questionStatus = 'incorrect';
      }
    } else {
      unattempted++;
      questionScore = question.markingScheme?.unattempted || markingScheme.unattempted;
      questionStatus = 'unattempted';
    }

    // Ensure questionScore is a number
    const numericQuestionScore = Number(questionScore) || 0;
    totalScore += numericQuestionScore;

    // Store detailed result for this question
    detailedResults.push({
      questionId: question.id,
      questionNumber: index + 1,
      isCorrect,
      userAnswer: isAttempted ? userAnswer : null, // Ensure unattempted questions have null userAnswer
      correctAnswer: question.correctAnswer,
      questionScore: numericQuestionScore,
      timeSpent: questionTimeSpent,
      difficulty: question.difficulty || 'medium',
      topic: question.topic || 'General',
      status: questionStatus,
      explanation: question.explanation || ''
    });
  });

  // Ensure totalScore is a number and not NaN
  totalScore = Number(totalScore) || 0;
  if (isNaN(totalScore)) {
    totalScore = 0;
  }

  // Calculate accuracy
  const attempted = correctAnswers + wrongAnswers;
  const accuracy = attempted > 0 ? Math.round((correctAnswers / attempted) * 100) : 0;

  // Calculate percentage score
  const totalPossibleMarks = questions.reduce((sum, q) => {
    return sum + (q.markingScheme?.correct || markingScheme.correct);
  }, 0);
  const percentageScore = totalPossibleMarks > 0 ? Math.round((totalScore / totalPossibleMarks) * 100) : 0;

  // Calculate sectional results if test has sections configured
  let sectionalResults = [];

  // Debug sectional calculation - removed
  // Sectional calculation analysis
  
  if (testConfig && testConfig.sections && testConfig.sections.length > 0) {
    // Import the sectional analysis function
    const { calculateSectionalResults } = await import('./sectionalAnalysis.js');
    sectionalResults = calculateSectionalResults(testConfig, userAnswers, questions, {
      score: totalScore,
      totalMarks: totalPossibleMarks,
      correctAnswers,
      wrongAnswers,
      unattempted,
      timeSpent: actualTimeSpent
    });
  } else if (testData.useDynamicSections && testData.sections && testData.sections.length > 0) {
    // Use test data sections if testConfig is not available but test has sections
    const { calculateSectionalResults } = await import('./sectionalAnalysis.js');
    const testDataConfig = {
      sections: testData.sections
    };
    sectionalResults = calculateSectionalResults(testDataConfig, userAnswers, questions, {
      score: totalScore,
      totalMarks: totalPossibleMarks,
      correctAnswers,
      wrongAnswers,
      unattempted,
      timeSpent: actualTimeSpent
    });
  } else if (questions.length > 0) {
    // Create a default section if no sections are configured but we have questions
    const defaultTestConfig = {
      sections: [{
        id: 'default',
        name: 'All Questions',
        type: 'general',
        startQuestion: 1,
        endQuestion: questions.length,
        totalMarks: questions.length * 4
      }]
    };
    
    const { calculateSectionalResults } = await import('./sectionalAnalysis.js');
    sectionalResults = calculateSectionalResults(defaultTestConfig, userAnswers, questions, {
      score: totalScore,
      totalMarks: totalPossibleMarks,
      correctAnswers,
      wrongAnswers,
      unattempted,
      timeSpent: actualTimeSpent
    });
  } else {
  }

  return {
    // Basic test info
    testId: testData.id || testData.testId, // Will be overridden by TestInterface if undefined
    testName: title || '',
    exam: exam || '',
    testType: 'Mock Test',
    
    // User info
    userId: userData?.uid, // Will be overridden by TestInterface if undefined
    userName: userData?.displayName || userData?.email || '',
    userEmail: userData?.email || '',
    
    // Performance metrics
    score: totalScore,
    totalMarks: totalPossibleMarks,
    percentageScore,
    correctAnswers,
    wrongAnswers,
    unattempted,
    attempted,
    accuracy,
    
    // Time metrics
    totalTimeSpent: totalTimeSpent, // Use sum of individual question times
    timeSpent: totalTimeSpent, // Alias for compatibility with results display
    totalTime: duration, // Total time allowed for the test (in seconds)
    timeUtilization,
    startTime: new Date(startTime),
    endTime: new Date(endTime),
    
    // Detailed data
    detailedResults,
    sectionalResults,
    testConfig: testConfig || {
      sections: questions.length > 0 ? [{
        id: 'default',
        name: 'All Questions',
        type: 'general',
        startQuestion: 1,
        endQuestion: questions.length,
        totalMarks: questions.length * 4
      }] : []
    },
    
    // User answers for review
    answers,
    questionTimes: timeSpent, // Individual question times (renamed to avoid conflict)
    
    // Metadata
    completedAt: new Date(),
    version: '1.0'
  };
};

/**
 * Check if user answer is correct based on question type
 * @param {Object} question - Question object
 * @param {any} userAnswer - User's answer
 * @returns {boolean} Whether answer is correct
 */
const checkAnswerCorrectness = (question, userAnswer) => {
  const { type, correctAnswer } = question;

  // Debug logging removed

  let isCorrect = false;

  switch (type) {
    case 'multiple-choice':
      isCorrect = userAnswer === correctAnswer;
      break;
    
    case 'multiple-choice-multi':
      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        isCorrect = userAnswer.length === correctAnswer.length &&
               userAnswer.every(ans => correctAnswer.includes(ans));
      } else {
        isCorrect = false;
      }
      break;
    
    case 'integer':
    case 'floating-point':
      isCorrect = parseFloat(userAnswer) === parseFloat(correctAnswer);
      break;
    
    case 'subjective':
      isCorrect = userAnswer.toLowerCase().trim() === correctAnswer.toLowerCase().trim();
      break;
    
    case 'matrix-match':
      // For matrix match, compare arrays
      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        isCorrect = JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswer.sort());
      } else {
        isCorrect = false;
      }
      break;
    
    case 'assertion-reason':
      isCorrect = userAnswer === correctAnswer;
      break;
    
    default:
      isCorrect = userAnswer === correctAnswer;
  }

  return isCorrect;
};

/**
 * Calculate sectional results based on test configuration
 * @param {Array} sections - Test sections configuration
 * @param {Array} detailedResults - Detailed question results
 * @param {Array} questions - Questions array
 * @returns {Array} Sectional results
 */
const calculateSectionalResults = (sections, detailedResults, questions) => {
  return sections.map(section => {
    const startIndex = section.startQuestion - 1;
    const endIndex = section.endQuestion - 1;
    
    // Get questions for this section
    const sectionQuestions = questions.slice(startIndex, endIndex + 1);
    const sectionResults = detailedResults.slice(startIndex, endIndex + 1);
    
    // Calculate section metrics
    const sectionCorrect = sectionResults.filter(r => r.isCorrect).length;
    const sectionAttempted = sectionResults.filter(r => r.status !== 'unattempted').length;
    const sectionScore = sectionResults.reduce((sum, r) => sum + r.questionScore, 0);
    const sectionTimeSpent = sectionResults.reduce((sum, r) => sum + r.timeSpent, 0);
    
    const sectionAccuracy = sectionAttempted > 0 ? Math.round((sectionCorrect / sectionAttempted) * 100) : 0;
    const sectionTotalMarks = sectionQuestions.reduce((sum, q) => {
      return sum + (q.markingScheme?.correct || 4);
    }, 0);

    // Calculate totalTime for section - timeLimit is usually in minutes, convert to seconds
    let sectionTotalTime = sectionQuestions.length * 60; // Default: 60 seconds per question
    if (section.timeLimit) {
      // If timeLimit < 1000, it's likely in minutes, convert to seconds
      sectionTotalTime = section.timeLimit < 1000 ? section.timeLimit * 60 : section.timeLimit;
    }
    
    return {
      id: section.id,
      name: section.name,
      type: section.type,
      score: sectionScore,
      totalMarks: sectionTotalMarks,
      correctAnswers: sectionCorrect,
      wrongAnswers: sectionAttempted - sectionCorrect,
      unattempted: sectionQuestions.length - sectionAttempted,
      attempted: sectionAttempted,
      totalQuestions: sectionQuestions.length,
      accuracy: sectionAccuracy,
      timeSpent: sectionTimeSpent,
      totalTime: sectionTotalTime,
      startQuestion: section.startQuestion,
      endQuestion: section.endQuestion
    };
  });
};

/**
 * Calculate test statistics from all attempts
 * @param {Array} allResults - All test results for this test
 * @returns {Object} Test statistics
 */
export const calculateTestStatistics = (allResults) => {
  if (!allResults || allResults.length === 0) {
    return {
      totalAttempts: 0,
      averageScore: 0,
      averageAccuracy: 0,
      averageTime: 0,
      highestScore: 0,
      lowestScore: 0,
      marksDistribution: [],
      sectionalStats: []
    };
  }

  // Filter out invalid results and ensure we have unique results per user
  // Group by userId to avoid counting the same user multiple times
  const uniqueResults = [];
  const seenUsers = new Set();
  
  allResults.forEach(result => {
    const userId = result.userId || result.user?.uid || result.user?.id;
    if (userId && !seenUsers.has(userId)) {
      seenUsers.add(userId);
      uniqueResults.push(result);
    } else if (!userId) {
      // If no userId, include it (might be anonymous or old data)
      uniqueResults.push(result);
    }
  });

  const scores = uniqueResults.map(r => Number(r.score) || 0).filter(s => !isNaN(s));
  const accuracies = uniqueResults.map(r => Number(r.accuracy) || 0).filter(a => !isNaN(a));
  const times = uniqueResults.map(r => Number(r.totalTimeSpent) || 0).filter(t => !isNaN(t));
  
  // Extract totalMarks from results (use the most common value or default to 100)
  const totalMarksValues = uniqueResults
    .map(r => r.totalMarks)
    .filter(m => m !== undefined && m !== null);
  const totalMarks = totalMarksValues.length > 0 
    ? Math.max(...totalMarksValues) // Use the maximum total marks found
    : 100; // Default to 100 if not available

  // Calculate basic stats
  const totalAttempts = uniqueResults.length;
  const averageScore = totalAttempts > 0 ? Math.round(scores.reduce((sum, s) => sum + s, 0) / totalAttempts) : 0;
  const averageAccuracy = totalAttempts > 0 ? Math.round(accuracies.reduce((sum, a) => sum + a, 0) / totalAttempts) : 0;
  const averageTime = totalAttempts > 0 ? Math.round(times.reduce((sum, t) => sum + t, 0) / totalAttempts) : 0;
  const highestScore = scores.length > 0 ? Math.max(...scores) : 0;
  const lowestScore = scores.length > 0 ? Math.min(...scores) : 0;

  // Calculate marks distribution with dynamic total marks
  const marksDistribution = calculateMarksDistribution(scores, totalMarks);

  // Calculate sectional statistics if available
  const sectionalStats = calculateSectionalStatistics(uniqueResults);

  return {
    totalAttempts,
    averageScore,
    averageAccuracy,
    averageTime,
    highestScore,
    lowestScore,
    marksDistribution,
    sectionalStats,
    lastUpdated: new Date()
  };
};

/**
 * Calculate marks distribution for visualization
 * @param {Array} scores - Array of scores
 * @param {Number} totalMarks - Total marks for the exam (default: 100)
 * @returns {Array} Marks distribution data
 */
const calculateMarksDistribution = (scores, totalMarks = 100) => {
  if (!scores || scores.length === 0) {
    return [];
  }
  
  // Convert all scores to numbers and filter out invalid values
  // Keep original array to count actual students (including duplicates if same score)
  const numericScores = scores.map(s => Number(s)).filter(s => !isNaN(s));
  
  if (numericScores.length === 0) {
    return [];
  }
  
  // Find actual min and max scores from data
  const actualMinScore = Math.min(...numericScores);
  const actualMaxScore = Math.max(...numericScores);
  const maxScore = Math.max(totalMarks, actualMaxScore);
  
  // Calculate minimum possible score (negative marks possible)
  // Estimate worst case based on total marks
  const estimatedMinScore = Math.floor(-maxScore / 5);
  const minScore = actualMinScore < 0 
    ? Math.min(estimatedMinScore, Math.floor(actualMinScore / 10) * 10 - 10)
    : estimatedMinScore;
  
  const ranges = [];
  
  // Negative ranges (2 ranges below 0) - ensure no overlap
  if (minScore < 0) {
    const negativeRangeSize = Math.max(10, Math.ceil(Math.abs(minScore) / 2));
    let negativeStart = minScore;
    for (let i = 0; i < 2; i++) {
      const start = negativeStart;
      const end = i === 1 ? -1 : Math.min(-1, start + negativeRangeSize - 1);
      if (start <= end) {
        ranges.push({
          min: start,
          max: end,
          label: `${start}-${end}`
        });
        negativeStart = end + 1;
        if (negativeStart >= 0) break;
      }
    }
  }
  
  // Positive ranges (10 ranges from 0 to maxScore) - ensure no overlap and proper boundaries
  const positiveRangeSize = Math.max(1, Math.ceil(maxScore / 10));
  for (let i = 0; i < 10; i++) {
    const start = i * positiveRangeSize;
    const end = i === 9 ? maxScore : Math.min(maxScore, (i + 1) * positiveRangeSize - 1);
    if (start <= end) {
      ranges.push({
        min: start,
        max: end,
        label: `${start}-${end}`
      });
    }
  }

  // Count students in each range - ensure each score occurrence is counted only once
  // Use a map to track which score index has been assigned to a range
  const scoreToRangeMap = new Map();
  
  // First pass: assign each score occurrence (by index) to exactly one range (the first matching range)
  numericScores.forEach((score, index) => {
    // Only process if this score index hasn't been assigned yet
    if (!scoreToRangeMap.has(index)) {
      for (let j = 0; j < ranges.length; j++) {
        const range = ranges[j];
        if (score >= range.min && score <= range.max) {
          scoreToRangeMap.set(index, j);
          break; // Assign to first matching range only, then stop
        }
      }
    }
  });
  
  // Count students per range
  const rangeCounts = new Array(ranges.length).fill(0);
  scoreToRangeMap.forEach((rangeIndex) => {
    rangeCounts[rangeIndex]++;
  });

  return ranges.map((range, index) => ({
    range: range.label,
    students: rangeCounts[index] || 0
  }));
};

/**
 * Calculate sectional statistics
 * @param {Array} allResults - All test results
 * @returns {Array} Sectional statistics
 */
const calculateSectionalStatistics = (allResults) => {
  // Get all unique sections from results
  const allSections = new Set();
  allResults.forEach(result => {
    if (result.sectionalResults) {
      result.sectionalResults.forEach(section => {
        allSections.add(section.name);
      });
    }
  });

  return Array.from(allSections).map(sectionName => {
    const sectionResults = allResults
      .filter(result => result.sectionalResults)
      .map(result => result.sectionalResults.find(s => s.name === sectionName))
      .filter(Boolean);

    if (sectionResults.length === 0) return null;

    const scores = sectionResults.map(s => s.score);
    const accuracies = sectionResults.map(s => s.accuracy);
    const times = sectionResults.map(s => s.timeSpent);

    return {
      name: sectionName,
      totalAttempts: sectionResults.length,
      averageScore: Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length),
      averageAccuracy: Math.round(accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length),
      averageTime: Math.round(times.reduce((sum, t) => sum + t, 0) / times.length),
      highestScore: Math.max(...scores),
      lowestScore: Math.min(...scores)
    };
  }).filter(Boolean);
};

/**
 * Get user rank and percentile
 * @param {number} userScore - User's score
 * @param {Array} allScores - All scores for this test
 * @returns {Object} Rank and percentile
 */
export const calculateRankAndPercentile = (userScore, allScores) => {
  if (!allScores || allScores.length === 0) {
    return { rank: 1, totalParticipants: 1, percentile: 100 };
  }

  // Step 1: Sort scores in descending order (highest first)
  const sortedScores = [...allScores].sort((a, b) => b - a);
  const totalParticipants = sortedScores.length;
  
  // Step 2: Create rank mapping - highest score gets rank 1, second gets rank 2, etc.
  const rankMapping = new Map();
  let currentRank = 1;
  
  for (let i = 0; i < sortedScores.length; i++) {
    const score = sortedScores[i];
    
    // If this is the first occurrence of this score, assign the current rank
    if (!rankMapping.has(score)) {
      rankMapping.set(score, currentRank);
    }
    
    // Move to next rank for the next unique score
    if (i < sortedScores.length - 1 && sortedScores[i + 1] < score) {
      currentRank = i + 2; // Next rank
    }
  }
  
  // Step 3: Get user's rank from the mapping
  const userRank = rankMapping.get(userScore);
  
  if (userRank === undefined) {
    // Count how many scores are higher than user's score
    const higherScores = sortedScores.filter(score => score > userScore);
    const calculatedRank = higherScores.length + 1;
    
    return {
      rank: calculatedRank,
      totalParticipants,
      percentile: totalParticipants === 1 ? 100 : Math.round(((totalParticipants - calculatedRank) / (totalParticipants - 1)) * 100)
    };
  }
  
  // Step 4: Calculate percentile based on the mapped rank
  const percentile = totalParticipants === 1 
    ? 100 
    : Math.round(((totalParticipants - userRank) / (totalParticipants - 1)) * 100);

  return {
    rank: userRank,
    totalParticipants,
    percentile
  };
};

/**
 * Save test result to Firebase with statistics
 * @param {Object} testResult - Calculated test result
 * @param {Array} allResults - All existing results for this test
 * @returns {Promise} Save result
 */
export const saveTestResultWithStats = async (testResult, allResults = []) => {
  try {
    // Calculate rank and percentile
    const allScores = allResults.map(r => r.score);
    const { rank, totalParticipants, percentile } = calculateRankAndPercentile(testResult.score, allScores);
    
    // Add rank and percentile to result
    const resultWithRank = {
      ...testResult,
      rank,
      totalParticipants,
      percentile
    };

    // Save individual result
    const saveResult = await saveTestResult(resultWithRank);
    
    if (saveResult.success) {
      // Update test statistics
      const updatedResults = [...allResults, resultWithRank];
      const testStats = calculateTestStatistics(updatedResults);
      
      // Save updated statistics (you'll need to implement this function)
      // await updateTestStatistics(testResult.testId, testStats);
      
      return {
        success: true,
        result: resultWithRank,
        statistics: testStats
      };
    } else {
      return saveResult;
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};
